import { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useApp, useAppPath } from './AppContext';

// Coach mode is everything under /coach (its own tabs and dark bar); the rest is the personal app. The mode
// is the path, so reloads, back and notification links keep it. Where the app opens is the coach's
// launch_mode (coach | personal | last); "last" is the mode last used on this device, kept here (coach mode
// on a device that hasn't been used yet, or after signing out).
const LAST_MODE_KEY = 'macro_mate_mode';
const COACH_PREFIX = /^\/coach(\/|$)/;

export const isCoachPath = (pathAfterBase) => COACH_PREFIX.test(pathAfterBase) && !pathAfterBase.startsWith('/coach/intro');

export function lastMode() {
  try {
    return window.localStorage.getItem(LAST_MODE_KEY);
  } catch {
    return null;
  }
}

export function rememberMode(mode) {
  try {
    window.localStorage.setItem(LAST_MODE_KEY, mode);
  } catch {
    // Private mode or storage blocked: "last used" falls back to the personal app.
  }
}

// Clears the device's "last used" mode, so the next account to sign in here opens where its own launch_mode says.
export function forgetMode() {
  try {
    window.localStorage.removeItem(LAST_MODE_KEY);
  } catch {
    // Storage blocked: nothing was kept.
  }
}

// Set once the first screen of this app start has shown: only an app that starts on the bare home redirects.
let launched = false;

// A new sign-in in the native app mounts the app again without reloading the page: it counts as a new start.
export function startLaunch() {
  launched = false;
}

// Keeps the device's "last used" mode up to date as the coach moves around (both shells call it), and tells
// the app whether a coach-mode screen is showing, where the personal app's prompts wait.
export function useRememberMode() {
  const { coach, base, editable, coachClient, setCoachView } = useApp();
  const { pathname } = useLocation();
  useEffect(() => { launched = true; }, []);
  useEffect(() => {
    setCoachView?.(!!coachClient || isCoachPath(pathname.slice(base.length)));
  }, [setCoachView, coachClient, base, pathname]);
  useEffect(() => {
    if (!coach || !editable) return;
    rememberMode(isCoachPath(pathname.slice(base.length)) ? 'coach' : 'personal');
  }, [coach, editable, base, pathname]);
}

// Where a coach's app opens, decided once per start and only when it starts on the bare home (/me), so a link
// into the personal app still opens there and going Home later stays Home.
export function LaunchRedirect({ children }) {
  const { coach, editable } = useApp();
  const path = useAppPath();
  const first = useRef(!launched);
  // Only the first screen decides: coach mode turned on later (the role question) doesn't redirect from here.
  useEffect(() => { first.current = false; }, []);
  if (!first.current || !editable || !coach) return children;
  const mode = coach.launch_mode === 'last' ? (lastMode() || 'coach') : coach.launch_mode;
  return mode === 'coach' ? <Navigate to={path('/coach')} replace /> : children;
}
